import { CacheMetadata } from './types';

/**
 * Serializes data for cache storage
 */
export function serializeCacheData<T>(data: T): string {
  try {
    return JSON.stringify(data);
  } catch (error) {
    console.warn('Failed to serialize cache data:', error);
    throw error;
  }
}

/**
 * Deserializes data from cache storage
 */
export function deserializeCacheData<T>(serializedData: string): T {
  try {
    return JSON.parse(serializedData);
  } catch (error) {
    console.warn('Failed to deserialize cache data:', error);
    throw error;
  }
}

/**
 * Checks if cache entry is expired
 */
export function isCacheExpired(metadata: CacheMetadata, maxAge: number = 24 * 60 * 60 * 1000): boolean {
  return Date.now() - metadata.timestamp > maxAge;
}

/**
 * Generates a cache key from components
 */
export function generateCacheKey(userId: string, syncHash: string, serverFilterHash: string): string {
  return `${userId}_${syncHash}_${serverFilterHash}`;
}

/**
 * Parses a cache key into its components
 */
export function parseCacheKey(cacheKey: string): { userId: string; syncHash: string; serverFilterHash: string } | null {
  const parts = cacheKey.split('_');
  if (parts.length >= 3) {
    return {
      userId: parts[0],
      syncHash: parts[1],
      serverFilterHash: parts.slice(2).join('_') // In case filter hash contains underscores
    };
  }
  return null;
}

/**
 * Validates cache metadata for consistency
 */
export function validateCacheMetadata(metadata: CacheMetadata): boolean {
  return !!(
    metadata.userId &&
    metadata.syncHash &&
    metadata.serverFilterHash !== undefined &&
    metadata.timestamp &&
    metadata.dataSourceName
  );
}

/**
 * Creates cache metadata object
 */
export function createCacheMetadata(
  userId: string,
  syncHash: string,
  serverFilterHash: string,
  dataSourceName: string,
  isPartialData: boolean = false,
  nextToken?: string | null
): CacheMetadata {
  return {
    userId,
    syncHash,
    serverFilterHash,
    timestamp: Date.now(),
    dataSourceName,
    isPartialData,
    nextToken
  };
}

/**
 * Estimates cache entry size in bytes (rough approximation)
 */
export function estimateCacheSize<T>(data: T, metadata: CacheMetadata): number {
  try {
    const dataSize = JSON.stringify(data).length * 2; // Rough UTF-16 byte estimate
    const metadataSize = JSON.stringify(metadata).length * 2;
    return dataSize + metadataSize;
  } catch (error) {
    return 0;
  }
}

/**
 * Cache cleanup strategies
 */
export const CacheCleanupStrategies = {
  /**
   * Least Recently Used - removes oldest entries first
   */
  LRU: 'lru',
  
  /**
   * Time-based - removes entries older than specified age
   */
  TIME_BASED: 'time_based',
  
  /**
   * Size-based - removes entries when total size exceeds limit
   */
  SIZE_BASED: 'size_based'
} as const;

export type CacheCleanupStrategy = typeof CacheCleanupStrategies[keyof typeof CacheCleanupStrategies];